import React from 'react'

import { FaLinkedinIn } from 'react-icons/fa'
import {
  HiOutlineCheck,
  HiOutlineNewspaper,
  HiOutlineSignal,
  HiShieldCheck,
  HiSparkles,
} from 'react-icons/hi2'
import { LuWind } from 'react-icons/lu'

import Button from '@/components/common/Button'

import { cn } from '@/helpers/shadcn/utils'

const RISE_ANIMATION =
  'animate-in fade-in slide-in-from-bottom-2 duration-[380ms]'

const riseDelay = (ms: number): React.CSSProperties => ({
  animationDelay: `${ms}ms`,
  animationFillMode: 'backwards',
})

interface PromptBarProps {
  query: string
}

// The AI prompt bar shared by the first two step visuals — a typed query with
// a blinking caret. Matches the AI-prompt treatment in MonitorsIntroModal.
const PromptBar: React.FC<PromptBarProps> = ({ query }) => (
  <div className="flex h-[34px] w-full items-center gap-2 rounded-[9px] border border-violet-500/40 bg-white px-[11px] shadow-[0_0_0_4px_rgba(124,58,237,0.08)] dark:bg-primary-900">
    <HiSparkles className="h-[15px] w-[15px] shrink-0 text-violet-600" />
    <span className="text-[12.5px] text-primary-900 dark:text-primary-100">
      {query}
      <span className="ml-0.5 inline-block h-[13px] w-px -translate-y-px animate-pulse bg-violet-600 align-middle" />
    </span>
  </div>
)

interface ResultRowProps {
  delayMs: number
  badge: React.ReactNode
  name: string
  detail: string
  trailing?: React.ReactNode
}

const ResultRow: React.FC<ResultRowProps> = ({
  delayMs,
  badge,
  name,
  detail,
  trailing,
}) => (
  <div
    style={riseDelay(delayMs)}
    className={cn(
      'flex items-center gap-2.5 rounded-[9px] border border-primary-200 bg-white px-[11px] py-2 shadow-sm dark:border-primary-800 dark:bg-primary-900',
      RISE_ANIMATION,
    )}
  >
    {badge}
    <div className="flex min-w-0 flex-1 flex-col justify-center">
      <div className="truncate text-[12.5px] font-semibold leading-4 text-primary-900 dark:text-primary-100">
        {name}
      </div>
      <div className="mt-0.5 truncate text-[10px] leading-3 text-primary-400 dark:text-primary-500">
        {detail}
      </div>
    </div>
    {trailing}
  </div>
)

// Company "favicon" tile matching the app's vendor-favicon avatars (white
// tile in both themes so the logo backdrop stays seamless). The sample
// companies are fictional, so an inline logo mark stands in for the favicon.
const FaviconTile: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-[7px] border border-primary-200 bg-white dark:border-primary-700">
    {children}
  </span>
)

// These decorative controls use the same compact outline button as the app.
const SourceButton: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <Button
    type="button"
    variant="outline-compact"
    tabIndex={-1}
    aria-hidden={true}
    className="pointer-events-none shrink-0 text-[10px]"
  >
    {children}
  </Button>
)

// Step 1 — a plain-English web search returning company results.
export const CompanySearchVisual: React.FC = () => (
  <div className="absolute inset-0 flex flex-col items-center justify-center pt-8">
    <div className="flex w-[340px] max-w-[calc(100%-32px)] flex-col gap-[9px]">
      <PromptBar query="Find cybersecurity companies in Virginia" />
      <div className="flex w-full flex-col gap-1.5">
        <ResultRow
          delayMs={60}
          badge={
            <FaviconTile>
              <LuWind className="h-4 w-4 text-blue-600" />
            </FaviconTile>
          }
          name="Northwind Defense Systems"
          detail="Zero-trust security · Reston, VA"
          trailing={<SourceButton>Website</SourceButton>}
        />
        <ResultRow
          delayMs={150}
          badge={
            <FaviconTile>
              <HiShieldCheck className="h-4 w-4 text-emerald-600" />
            </FaviconTile>
          }
          name="Aegis Cyber Group"
          detail="Cloud security · Arlington, VA"
          trailing={<SourceButton>Profile</SourceButton>}
        />
      </div>
    </div>
  </div>
)

// Row-avatar-sized version of the app's LinkedIn badge (see library-v2
// PersonPage header) — the person results come with LinkedIn profiles.
const LINKEDIN_AVATAR = (
  <span
    aria-hidden={true}
    className="flex h-7 w-7 shrink-0 items-center justify-center rounded-[7px] bg-[#0A66C2] text-white"
  >
    <FaLinkedinIn size={15} />
  </span>
)

// Step 2 — an agency people lookup returning reachable contacts.
export const PeopleSearchVisual: React.FC = () => (
  <div className="absolute inset-0 flex flex-col items-center justify-center pt-8">
    <div className="flex w-[340px] max-w-[calc(100%-32px)] flex-col gap-[9px]">
      <PromptBar query="Who runs contracting at DISA?" />
      <div className="flex w-full flex-col gap-1.5">
        <ResultRow
          delayMs={60}
          badge={LINKEDIN_AVATAR}
          name="Marcus Reyes"
          detail="Contracting Officer · DISA"
        />
        <ResultRow
          delayMs={150}
          badge={LINKEDIN_AVATAR}
          name="Tara Chen"
          detail="Program Manager · DISA JITC"
        />
      </div>
    </div>
  </div>
)

// Step 3 — a recency-aware Exa search returning relevant news coverage.
export const NewsSearchVisual: React.FC = () => (
  <div className="absolute inset-0 flex flex-col items-center justify-center pt-8">
    <div className="flex w-[340px] max-w-[calc(100%-32px)] flex-col gap-[9px]">
      <PromptBar query="What's new in federal cybersecurity?" />
      <div className="flex w-full flex-col gap-1.5">
        <ResultRow
          delayMs={60}
          badge={
            <FaviconTile>
              <HiOutlineNewspaper className="h-4 w-4 text-blue-600" />
            </FaviconTile>
          }
          name="Zero-trust deadlines reshape priorities"
          detail="Federal News Network · 2 hours ago"
          trailing={<SourceButton>Read</SourceButton>}
        />
        <ResultRow
          delayMs={150}
          badge={
            <FaviconTile>
              <HiOutlineSignal className="h-4 w-4 text-rose-600" />
            </FaviconTile>
          }
          name="New cloud security guidance lands"
          detail="Nextgov/FCW · Today"
          trailing={<SourceButton>Read</SourceButton>}
        />
      </div>
    </div>
  </div>
)

const RESEARCH_DONE_LINES = [
  'Split into 3 research tasks',
  'Scanned 42 awards on USASpending',
  'Pulled leadership from company site',
]

// Step 3 — a deep research run working public sources into a cited brief.
export const ResearchVisual: React.FC = () => (
  <div className="absolute inset-0 flex items-center justify-center">
    <div className="relative w-[340px] max-w-[calc(100%-32px)] overflow-hidden rounded-[13px] border border-violet-500/40 bg-white shadow-[0_0_0_4px_rgba(124,58,237,0.08)] dark:bg-primary-900">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-[40%] animate-scan-down bg-gradient-to-b from-transparent to-violet-500/[0.08]" />
      <div className="relative px-3.5 py-3">
        <div className="flex items-center gap-2">
          <span
            style={
              {
                '--ripple-color': 'rgba(124,58,237,0.4)',
                '--ripple-radius': '6px',
                '--ripple-time': '2.4s',
              } as React.CSSProperties
            }
            className="flex h-6 w-6 animate-ripple items-center justify-center rounded-[7px] bg-violet-600/[0.12]"
          >
            <HiSparkles className="h-3.5 w-3.5 text-violet-600" />
          </span>
          <span className="text-[11.5px] font-semibold text-violet-600 dark:text-violet-400">
            Researching Northwind Defense Systems
          </span>
        </div>
        <div className="mt-[11px] flex flex-col gap-[7px]">
          {RESEARCH_DONE_LINES.map((line, index) => (
            <div
              key={line}
              style={riseDelay(60 + index * 160)}
              className={cn(
                'flex items-center gap-[7px] text-[11px] text-primary-600 dark:text-primary-300',
                RISE_ANIMATION,
              )}
            >
              <HiOutlineCheck className="h-[13px] w-[13px] shrink-0 stroke-[2.4] text-green-600" />
              {line}
            </div>
          ))}
          <div
            style={riseDelay(540)}
            className={cn(
              'flex items-center gap-[7px] text-[11px] text-primary-400 dark:text-primary-500',
              RISE_ANIMATION,
            )}
          >
            <span className="h-[13px] w-[13px] shrink-0 animate-spin rounded-full border-2 border-violet-600/[0.28] border-t-violet-600" />
            <span>
              Compiling sourced brief…
              <span className="ml-0.5 inline-block h-[11px] w-px -translate-y-px animate-pulse bg-violet-600 align-middle" />
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
)
